"use client";

import { useState, type FC, type Ref } from "react";
import { useTranslation } from "react-i18next";
import { HouseHeader } from "@huub-web/shared";
import {
  Flex,
  Heading,
  type HouseRecord,
  LinkButton,
  Spacing,
  Text,
  type UIAction,
  useActions,
  ValueParser,
  Icon,
  usePubSub,
  Modal,
  ProgressBar,
  Body,
  Button,
  type GalleryProps,
} from "@huub-web/ui";

import { useTemplates } from "../../hooks";

export interface AnalysisHeaderTemplateProps {
  record: HouseRecord;
  unlisted?: boolean;
  CTAs?: {
    maximumMortgage?: {
      label?: string;
      action: UIAction;
      tracking?: string;
    };
  };
  saveEndpoint?: string;
  deleteEndpoint?: string;
  saved?: boolean;
  summary?: string;
  images?: GalleryProps["images"];
}

/**
 * @deprecated
 */
export const AnalysisHeaderTemplate: FC<
  AnalysisHeaderTemplateProps & { ref?: Ref<HTMLDivElement> }
> = ({
  record,
  CTAs,
  unlisted,
  saveEndpoint,
  deleteEndpoint,
  saved: savedProp,
  images,
  ref,
}) => {
  const { t } = useTranslation();
  const { handleAction } = useActions();
  const { $post, $delete, lockUI } = useTemplates();
  const pubSub = usePubSub();

  const [isConfirmationActive, setIsConfirmationActive] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saved, setSaved] = useState(savedProp);

  const handleSaveToggle = () => {
    if (!saveEndpoint && !deleteEndpoint) {
      console.warn(
        "[HouseCardTemplate] No delete and/or save endpoint provided, skipping",
      );
      return;
    }

    if (!record.id) {
      console.error(
        "[HouseCardTemplate] Cannot update saved state: record.id is missing",
      );
      return;
    }

    const newState = !saved;

    if (newState) {
      if (!saveEndpoint) return;

      setLoading(true);

      $post(saveEndpoint, { id: record.id, saved: true })
        .then(() => {
          setSaved(true);
        })
        .catch((error) => {
          console.error("[HouseCardTemplate] Failed to save house", error);
          pubSub.publish("snackbar", {
            variant: "error",
            body: t("templating.property.saveError"),
          });
          setSaved(false);
        })
        .finally(() => {
          setLoading(false);
        });
    } else {
      if (!deleteEndpoint) {
        console.warn(
          "[HouseCardTemplate] No delete endpoint provided, cannot delete",
        );
        pubSub.publish("snackbar", {
          variant: "error",
          body: t("templating.property.deleteNotAllowed"),
        });
        return;
      }
      setIsConfirmationActive(true);
    }
  };

  const handleDeleteConfirm = () => {
    if (!deleteEndpoint) {
      console.error("[HouseCardTemplate] Delete endpoint is missing");
      setIsConfirmationActive(false);
      return;
    }

    setLoading(true);

    // Construct the delete URL with the record ID
    $delete(deleteEndpoint)
      .then(() => {
        setSaved(false);
      })
      .catch((error) => {
        console.error("[HouseCardTemplate] Failed to delete house", error);
        pubSub.publish("snackbar", {
          variant: "error",
          body: t("templating.property.deleteError"),
        });
      })
      .finally(() => {
        setLoading(false);
        setIsConfirmationActive(false);
      });
  };

  const [, setIsImagesModalOpen] = useState(false);

  return (
    <>
      <Flex ref={ref} direction="column" gap="l">
        <Heading variant="main" title={t("analysisHeader.title")} />

        <HouseHeader
          record={record}
          savable={(!!saveEndpoint && !saved) || (!!deleteEndpoint && saved)}
          saved={saved}
          onSaveToggle={handleSaveToggle}
          disabled={loading || isConfirmationActive || lockUI}
          titleTrailing={
            images &&
            images.length > 0 && (
              <Button
                onClick={() => setIsImagesModalOpen(true)}
                variant="opaque"
                border="default"
                size="s"
              >
                {t("analysisHeader.viewAllPhotos", { count: images.length })}
              </Button>
            )
          }
          card={
            record?.price &&
            record.price > 0 && (
              <Flex direction="column" gap="xxs" grow>
                <Text variant="bodyS" bold>
                  {unlisted
                    ? t("analysisHeader.lastKnownMarketValue")
                    : t("analysisHeader.askingPrice")}
                </Text>
                <Text variant="displayM" bold>
                  <ValueParser value={{ type: "money", value: record.price }} />
                </Text>
                <Spacing grow />
                {CTAs?.maximumMortgage &&
                  (() => {
                    const { action, label, tracking } = CTAs.maximumMortgage;

                    return (
                      <LinkButton
                        alignSelf="flex-start"
                        tracking={
                          tracking ?? "analysis_header_maximum_mortgage_button"
                        }
                        onClick={() => handleAction(action)}
                        trailing={<Icon icon="chevronRightThick" size="s" />}
                      >
                        {label ?? t("analysisHeader.calculateMaxMortgage")}
                      </LinkButton>
                    );
                  })()}
              </Flex>
            )
          }
          // chanceCardCTA={
          //   <AnchorButton
          //     variant="background"
          //     alignSelf="flex-start"
          //     tracking={`${trackingPrefix}_house_header_biedcertificaat_button`}
          //     href={`/adviseur-spreken/winnende-bod?addressObjectID=${record?.id}`}
          //   >
          //     Gratis biedcertificaat
          //   </AnchorButton>
          // }
        />
      </Flex>

      {isConfirmationActive && (
        <Modal placement="center">
          {loading && <ProgressBar variant="infinite" position="absolute" />}

          <Body>
            {t("templating.property.deleteConfirmation", {
              street: record.street,
            })}
          </Body>

          <Flex justify="space-between" align="flex-end" gap="s">
            <Button
              rel="noopener noreferrer"
              variant="background"
              border="default"
              onClick={() => setIsConfirmationActive(false)}
              disabled={loading}
            >
              {t("cancel")}
            </Button>
            <Button
              rel="noopener noreferrer"
              variant="error"
              trailing={<Icon icon="delete" size="s2" />}
              onClick={handleDeleteConfirm}
              disabled={loading}
            >
              {t("delete")}
            </Button>
          </Flex>
        </Modal>
      )}
    </>
  );
};
